import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { CustomInput } from "global/components";
import AppModal from "global/components/AppModal/AppModal";
import EmptyState from "global/components/EmptyState/EmptyState";
import customInputStyles from "global/components/CustomInput/CustomInput.styles";
import formFieldMetrics from "global/components/CustomInput/FormField.tokens";
import SettingsLayout from "../components/SettingsLayout";
import { useTitle } from "utils/UseTitle";
import strings from "global/constants/StringConstants";
import { hasAccessTo } from "layouts/Config";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { WorkspaceRole, WORKSPACE_PERMISSIONS } from "types/workspace";
import {
  createWorkspaceRole,
  deleteWorkspaceRole,
  fetchWorkspaceRoles,
  updateWorkspaceRole,
} from "./WorkspaceRolesServices";
import { useAppDispatch } from "redux/hooks";
import { setActiveWorkspace } from "redux/authSlice";
import { store } from "utils/store";
import WorkspacesBackLink from "./components/WorkspacesBackLink";
import WorkspaceListSkeleton from "./components/WorkspaceListSkeleton";
import workspacesTokens from "./Workspaces.tokens";
import {
  actionCellSx,
  cardSx,
  checkboxGroupSx,
  checkboxLabelSx,
  columnHeaderSx,
  formSelectSx,
  groupLabelSx,
  listRowSx,
  modalBodyTextSx,
  modalFieldsSx,
  noteSx,
  pageStackSx,
  pillSx,
  primaryButtonSx,
  rowActionSx,
  rowDangerActionSx,
  rowTextSx,
  visuallyHiddenSx,
} from "./Workspaces.styles";

type EditDialog = {
  mode: "create" | "edit" | "view";
  role?: WorkspaceRole;
} | null;

const GRID = workspacesTokens.list.grid.roles;
const { pill } = workspacesTokens;
const PERMISSIONS_GROUP_LABEL_ID = "workspace-role-permissions-label";
const REASSIGN_SELECT_ID = "workspace-role-reassign";

const WorkspaceRoles = () => {
  useTitle("Workspace Roles");
  const { id: workspaceId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const isAdmin = hasAccessTo(strings.resources.app, strings.adminPermission);

  const [roles, setRoles] = useState<WorkspaceRole[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [edit, setEdit] = useState<EditDialog>(null);
  const [nameInput, setNameInput] = useState<string>("");
  const [selectedPerms, setSelectedPerms] = useState<string[]>([]);

  const [toDelete, setToDelete] = useState<WorkspaceRole | null>(null);
  const [reassignTo, setReassignTo] = useState<number | "">("");

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchWorkspaceRoles();
      setRoles(Array.isArray(data) ? data : []);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Active so a Workspace Admin's role.manage applies in this workspace context — scoped to
    // this page's visit only. Restore whatever was active before on cleanup, so viewing a
    // workspace's roles doesn't silently and permanently switch the user's active workspace for
    // the rest of the session.
    const previousActiveWorkspaceId = store.getState().auth.activeWorkspaceId;
    if (workspaceId) {
      dispatch(setActiveWorkspace(workspaceId));
    }
    load();
    return () => {
      dispatch(setActiveWorkspace(previousActiveWorkspaceId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const permsOf = (role: WorkspaceRole): string[] => {
    if (Array.isArray(role.permissions)) return role.permissions;
    try {
      return JSON.parse(role.permissionsJson || "[]");
    } catch {
      return [];
    }
  };

  const openCreate = () => {
    setNameInput("");
    setSelectedPerms([]);
    setEdit({ mode: "create" });
  };

  const openEdit = (role: WorkspaceRole) => {
    setNameInput(role.name);
    setSelectedPerms(permsOf(role));
    setEdit({ mode: "edit", role });
  };

  const openView = (role: WorkspaceRole) => {
    setNameInput(role.name);
    setSelectedPerms(permsOf(role));
    setEdit({ mode: "view", role });
  };

  const isView = edit?.mode === "view";

  const togglePerm = (key: string) => {
    setSelectedPerms((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const submitEdit = async () => {
    if (!edit) return;
    const name = nameInput.trim();
    if (!name) return;
    try {
      setSubmitting(true);
      if (edit.mode === "create") {
        await createWorkspaceRole(name, selectedPerms);
        openSuccessNotification(`Role "${name}" created.`);
      } else if (edit.role) {
        await updateWorkspaceRole(edit.role.id, name, selectedPerms);
        openSuccessNotification("Role updated.");
      }
      setEdit(null);
      await load();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    if (!toDelete) return;
    try {
      setSubmitting(true);
      await deleteWorkspaceRole(
        toDelete.id,
        reassignTo === "" ? undefined : reassignTo,
      );
      openSuccessNotification(`Role "${toDelete.name}" deleted.`);
      setToDelete(null);
      setReassignTo("");
      await load();
    } catch (error: any) {
      // e.g. 409 when the role is in use and no reassignment target was chosen
      handleErrorMessage(error);
    } finally {
      setSubmitting(false);
    }
  };

  const reassignTargets = roles.filter((r) => r.id !== toDelete?.id);

  const headerAction = isAdmin ? (
    <Button
      variant="contained"
      startIcon={<AddRoundedIcon />}
      onClick={openCreate}
      sx={primaryButtonSx}
    >
      New role
    </Button>
  ) : undefined;

  const getRow = (role: WorkspaceRole, isLast: boolean) => {
    const permissionCount = permsOf(role).length;
    return (
      <Box key={role.id} sx={listRowSx(GRID, isLast)}>
        <Box component="span" sx={rowTextSx("ink", "strong")}>
          {role.name}
        </Box>
        <Box>
          <Box
            component="span"
            sx={pillSx(role.preset ? pill.neutral : pill.brand)}
          >
            {role.preset ? "Preset" : "Custom"}
          </Box>
        </Box>
        <Box component="span" sx={rowTextSx("body", "regular")}>
          {`${permissionCount} permission${permissionCount === 1 ? "" : "s"}`}
        </Box>
        <Box sx={actionCellSx}>
          {isAdmin && !role.preset ? (
            <>
              <Tooltip title="Edit" arrow>
                <IconButton
                  aria-label={`Edit ${role.name}`}
                  onClick={() => openEdit(role)}
                  sx={rowActionSx}
                >
                  <EditOutlinedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete" arrow>
                <IconButton
                  aria-label={`Delete ${role.name}`}
                  onClick={() => {
                    setReassignTo("");
                    setToDelete(role);
                  }}
                  sx={rowDangerActionSx}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="View permissions" arrow>
              <IconButton
                aria-label={`View ${role.name}`}
                onClick={() => openView(role)}
                sx={rowActionSx}
              >
                <VisibilityOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const renderRows = () => {
    if (loading) {
      return (
        <WorkspaceListSkeleton columns={GRID} middleCells={["50%", "60%"]} />
      );
    }
    if (roles.length === 0) {
      return (
        <EmptyState
          icon="shield"
          title="No roles yet"
          body="Roles decide what a member can do inside a workspace."
          primaryAction={
            isAdmin ? { label: "New role", onClick: openCreate } : undefined
          }
        />
      );
    }
    return roles.map((role, index) => getRow(role, index === roles.length - 1));
  };

  const editTitle =
    edit?.mode === "create"
      ? "New role"
      : isView
        ? edit?.role?.name ?? "Role"
        : "Edit role";

  return (
    <SettingsLayout
      activeTab="Workspaces"
      title="Workspace roles"
      headerAction={headerAction}
    >
      <WorkspacesBackLink />

      <Box sx={pageStackSx}>
        <Box sx={noteSx}>
          <InfoOutlinedIcon aria-hidden="true" />
          <Box component="span">
            These roles are <strong>shared across every workspace</strong> in
            your account — creating, editing or deleting a role here affects
            all workspaces. Who has which role is set per workspace, from its
            Members page.
          </Box>
        </Box>

        <Box sx={cardSx}>
          <Box sx={columnHeaderSx(GRID)}>
            <Box component="span">Name</Box>
            <Box component="span">Type</Box>
            <Box component="span">Permissions</Box>
            <Box component="span" sx={visuallyHiddenSx}>
              Actions
            </Box>
          </Box>
          <Box aria-busy={loading}>{renderRows()}</Box>
        </Box>
      </Box>

      {/* Create / edit / view */}
      <AppModal
        open={Boolean(edit)}
        onClose={() => setEdit(null)}
        title={editTitle}
        subtitle={isView ? "Preset roles can't be changed." : undefined}
        size="md"
        busy={submitting}
        primaryLabel={
          isView ? undefined : edit?.mode === "create" ? "Create role" : "Save role"
        }
        onPrimary={submitEdit}
        primaryLoading={submitting}
        primaryDisabled={!nameInput.trim()}
        cancelLabel={isView ? "Close" : "Cancel"}
      >
        <Box sx={modalFieldsSx}>
          <CustomInput
            autoFocus={!isView}
            label="Role name"
            placeHolder="e.g. Reviewer"
            name="workspaceRoleName"
            value={nameInput}
            onChange={(e: any) => setNameInput(e.target.value)}
            disabled={isView}
          />
          <Box>
            <Typography
              component="h3"
              id={PERMISSIONS_GROUP_LABEL_ID}
              sx={groupLabelSx}
            >
              Permissions
            </Typography>
            <FormGroup
              role="group"
              aria-labelledby={PERMISSIONS_GROUP_LABEL_ID}
              sx={checkboxGroupSx}
            >
              {WORKSPACE_PERMISSIONS.map((perm) => (
                <FormControlLabel
                  key={perm.key}
                  sx={checkboxLabelSx}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedPerms.includes(perm.key)}
                      onChange={() => togglePerm(perm.key)}
                      disabled={isView}
                    />
                  }
                  label={perm.label}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      </AppModal>

      {/* Delete + optional reassign */}
      <AppModal
        open={Boolean(toDelete)}
        onClose={() => setToDelete(null)}
        tone="destructive"
        title={`Delete "${toDelete?.name ?? ""}"?`}
        size="sm"
        busy={submitting}
        primaryLabel="Delete role"
        onPrimary={submitDelete}
        primaryLoading={submitting}
        cancelLabel="Cancel"
      >
        <Box sx={modalFieldsSx}>
          <Typography sx={modalBodyTextSx}>
            This removes the role from every workspace. If members hold it,
            choose a role to move them to.
          </Typography>
          <Box>
            <Box mb={formFieldMetrics.labelGap}>
              <InputLabel
                id={`${REASSIGN_SELECT_ID}-label`}
                sx={customInputStyles.inputLabel}
              >
                Move members to
              </InputLabel>
            </Box>
            <Select
              labelId={`${REASSIGN_SELECT_ID}-label`}
              id={REASSIGN_SELECT_ID}
              displayEmpty
              value={reassignTo}
              onChange={(e) =>
                setReassignTo(e.target.value === "" ? "" : Number(e.target.value))
              }
              sx={formSelectSx}
            >
              <MenuItem value="">No one holds this role</MenuItem>
              {reassignTargets.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </AppModal>
    </SettingsLayout>
  );
};

export default WorkspaceRoles;
